import React, { useContext, useEffect, useRef, useState } from 'react';
import { IFolderData } from '../utils/interface';
import { useHistory, useLocation } from 'react-router-dom';
import { Context } from '../MunicipalNoticeBoard';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faArrowRight, faList } from '@fortawesome/free-solid-svg-icons';
import {
    CarouselRowDiv,
    CarouselPageDiv,
    ContaineDiv,
    ArrowButton,
    ArrowIcon,
    CarouselDiv,
    ItemContainer,
    ItemDiv,
    ItemMain,
    NumberOfActiveDocs,
    ArrowIconDiv,
    NavDot,
    NavList,
    CarouselIconDiv,
    ListViewButton,
} from './StyledComponents';
import { CategoryIcon } from './Icon';

interface IContextProps {
    cols: number;
    rows: number;
    gap?: number;
    categories: IFolderData[];
    font?: string;
    children?: any;
}

enum EScrollPosition {
    begin,
    end,
    middle,
}

export const CarouselContext = React.createContext<Partial<IContextProps>>({});

export const Carousel: React.FC<IContextProps> = (props) => {
    const { cols, rows } = props;
    const [scrollPosition, setScrollPosition] = useState<EScrollPosition>(EScrollPosition.begin);
    const [scrollPage, setScrollPage] = useState<number>(0);
    const [fontSize, setFontSize] = useState('1vw');
    const carouselDivRef = useRef<HTMLDivElement>(null);
    const containerDivRef = useRef<HTMLDivElement>(null);

    const ctx = useContext(Context);
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const div = containerDivRef.current;
        if (div) {
            const divRatio = div.clientWidth / div.clientHeight;
            const colsRowsRatio = cols / rows;

            //heigth is the limiting factor
            if (divRatio > colsRowsRatio) {
                setFontSize(4 / rows + 'vh');
            }
            //width is the limiting factor
            else {
                setFontSize(4 / cols + 'vw');
            }
        }
    }, [rows, cols]);

    useEffect(() => {
        carouselDivRef.current && (carouselDivRef.current.scrollLeft = ctx.listScrollPosition || 0);
    }, [carouselDivRef]);

    const pages: JSX.Element[] = [];
    const pageSize = props.cols * props.rows;
    const pageCount = Math.ceil(props.categories.length / pageSize);
    for (let p = 0; p < pageCount; p++) {
        const rows: JSX.Element[] = [];
        for (let i = 0; i < props.rows; i++) {
            const start = p * pageSize + i * props.cols;
            const end = start + props.cols;
            rows.push(
                <CarouselRowDiv key={'row-' + p + '-' + i} rows={props.rows}>
                    {props.categories.slice(start, end).map((category) => (
                        <CarouselItem key={category._id} category={category} />
                    ))}
                </CarouselRowDiv>
            );
        }
        pages.push(
            <CarouselPageDiv key={'page-' + p + '-'} page={p}>
                {rows}
            </CarouselPageDiv>
        );
    }

    function scrollLeft() {
        if (scrollPosition !== EScrollPosition.begin) {
            const div = carouselDivRef.current;
            if (div) {
                scrollTo(div, div.scrollLeft - div.clientWidth);
            }
        }
    }

    function scrollRight() {
        if (scrollPosition !== EScrollPosition.end) {
            const div = carouselDivRef.current;
            if (div) {
                scrollTo(div, div.scrollLeft + div.clientWidth);
            }
        }
    }

    function scrollTo(div: HTMLDivElement, position: number) {
        if (typeof div.scroll === 'function') {
            div.scroll({ left: position, behavior: 'smooth' });
        } else {
            div.scrollLeft = position;
        }
    }

    function scrollToPage(page: number) {
        const div = carouselDivRef.current;
        if (div) {
            scrollTo(div, page * div.clientWidth);
        }
    }

    function onScroll(e: React.UIEvent<HTMLDivElement>) {
        const target: any = e.target || e.currentTarget;
        const scrollLeft: number = target.scrollLeft;

        ctx.setListScrollPosition && ctx.setListScrollPosition(scrollLeft || 0);

        if (scrollLeft === 0) {
            setScrollPosition(EScrollPosition.begin);
        } else if (scrollLeft + target.clientWidth >= target.scrollWidth) {
            setScrollPosition(EScrollPosition.end);
        } else {
            setScrollPosition(EScrollPosition.middle);
        }
        if (pageCount > 1) {
            const currentPage = Math.floor((scrollLeft + target.clientWidth * 0.5) / target.clientWidth);
            setScrollPage(currentPage);
        }
    }

    function onAllDocuments() {
        history.push({
            pathname: '/list',
            state: {},
            search: location.search,
        });
    }

    return (
        <CarouselContext.Provider value={props}>
            <ContaineDiv cols={props.cols} rows={props.rows} ref={containerDivRef} style={{ fontSize }}>
                <ArrowButton onClick={scrollLeft}>
                    <ArrowIcon
                        disabled={pageCount <= 1 || scrollPosition === EScrollPosition.begin}
                        className="click-animation">
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </ArrowIcon>
                </ArrowButton>
                <CarouselDiv
                    onScroll={debounce(onScroll, 150)}
                    ref={carouselDivRef}
                    cols={props.cols}
                    rows={props.rows}>
                    {' '}
                    {pages}
                </CarouselDiv>
                <ArrowButton onClick={scrollRight}>
                    <ArrowIcon
                        disabled={pageCount <= 1 || scrollPosition === EScrollPosition.end}
                        className="click-animation">
                        <FontAwesomeIcon icon={faAngleRight} />
                    </ArrowIcon>
                </ArrowButton>
                <PageIndicator pageCount={pageCount} currentPage={scrollPage} scrollToPage={scrollToPage} />
                <ListViewButton onClick={onAllDocuments}>
                    <FontAwesomeIcon icon={faList} />{' '}
                    <span style={props.font ? { fontFamily: props.font } : {}}>Alle Dokumente</span>{' '}
                </ListViewButton>
            </ContaineDiv>
        </CarouselContext.Provider>
    );
};

export const CarouselItem: React.FC<{ category: IFolderData }> = (props) => {
    const context = useContext(CarouselContext);
    const { icons, font } = useContext(Context);
    const history = useHistory();
    const location = useLocation();
    let iconSrc: string | undefined;
    if (props.category.iconId && icons?.has(props.category.iconId)) {
        iconSrc = icons.get(props.category.iconId)?.url;
    }

    function clickCategory() {
        history.push({
            pathname: '/list',
            state: { categoryId: props.category._id },
            search: location.search,
        });
    }

    const gap = context.gap || 4;

    return (
        <ItemContainer cols={context.cols || 4} onClick={() => clickCategory()}>
            <ItemDiv gap={gap} style={font ? { fontFamily: font } : {}} className="click-animation">
                <CarouselIconDiv>
                    <CategoryIcon src={iconSrc} />
                </CarouselIconDiv>
                <ItemMain style={font ? { fontFamily: font } : { }}>
                    {props.category.name}
                </ItemMain>
                <NumberOfActiveDocs style={{}}>{props.category.countDocuments}</NumberOfActiveDocs>
                <ArrowIconDiv>
                    <FontAwesomeIcon icon={faArrowRight} />
                </ArrowIconDiv>
            </ItemDiv>
        </ItemContainer>
    );
};

const PageIndicator: React.FC<{
    pageCount: number;
    currentPage: number;
    scrollToPage: (page: number) => void;
}> = ({ pageCount, currentPage, scrollToPage }) => {
    if (pageCount <= 1) {
        return <></>;
    }

    const indicators: JSX.Element[] = [];
    for (let i = 0; i < pageCount; i++) {
        indicators.push(<NavDot active={i === currentPage} key={'indicator-' + i} onClick={() => scrollToPage(i)} />);
    }

    return (
        <NavList>
            <ul>{indicators}</ul>
        </NavList>
    );
};
